import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
  Text,
  Avatar,
  Tooltip,
} from "@fluentui/react-components";
import {
  AlertRegular,
  BookRegular,
  PersonRegular,
  SettingsRegular,
  SignOutRegular,
} from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useAtom, useAtomValue } from "jotai";
import { useState } from "react";

import {
  GettingStartedDialog,
  LanguageSwitcher,
  PersonaSwitcher,
  PreferencesDialog,
  ThemeToggle,
} from "@/components/common";
import { onboardingAtom, personaAtom, unreadNotificationsAtom } from "@/store";
import { getUserManager } from "@/auth/oidcConfig";

const HEADER_HEIGHT = "56px";

const useStyles = makeStyles({
  header: {
    position: "fixed",
    top: "0",
    insetInlineStart: "0",
    insetInlineEnd: "0",
    height: HEADER_HEIGHT,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingInlineStart: tokens.spacingHorizontalL,
    paddingInlineEnd: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
    zIndex: 100,
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  identity: {
    display: "flex",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalXS,
  },
  roleText: {
    color: tokens.colorNeutralForeground3,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  notificationWrapper: {
    position: "relative",
  },
  badge: {
    position: "absolute",
    top: "-4px",
    insetInlineEnd: "-4px",
  },
});

export function Header() {
  const { t } = useTranslation();
  const styles = useStyles();
  const persona = useAtomValue(personaAtom);
  const unreadCount = useAtomValue(unreadNotificationsAtom);
  const [onboarding, setOnboarding] = useAtom(onboardingAtom);
  const [personaDialogOpen, setPersonaDialogOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [gettingStartedOpen, setGettingStartedOpen] = useState(false);

  const handleLogout = () => {
    getUserManager().signoutRedirect().catch(console.error);
  };

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <Avatar name={persona.name} size={32} color="brand" />
        <div className={styles.identity}>
          <Text weight="semibold" size={300}>
            {persona.name}
          </Text>
          <Text size={200} className={styles.roleText}>
            — {persona.roleLabel}
          </Text>
        </div>
      </div>
      <div className={styles.right}>
        <div className={styles.notificationWrapper}>
          <Tooltip content="Notifications" relationship="label">
            <Button
              icon={<AlertRegular />}
              appearance="subtle"
              aria-label="Notifications"
            />
          </Tooltip>
          {unreadCount > 0 && (
            <Badge
              className={styles.badge}
              size="tiny"
              color="danger"
              appearance="filled"
            />
          )}
        </div>
        <LanguageSwitcher />
        <ThemeToggle />
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content={t("settings")} relationship="label">
              <Button
                icon={<SettingsRegular />}
                appearance="subtle"
                aria-label={t("settings")}
              />
            </Tooltip>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem
                icon={<BookRegular />}
                onClick={() => setGettingStartedOpen(true)}
              >
                Getting Started
              </MenuItem>
              <MenuItem
                icon={<SettingsRegular />}
                onClick={() => setPreferencesOpen(true)}
              >
                Preferences
              </MenuItem>
              <MenuItem
                icon={<PersonRegular />}
                onClick={() => setPersonaDialogOpen(true)}
              >
                Switch Persona
              </MenuItem>
              <MenuItem icon={<SignOutRegular />} onClick={handleLogout}>
                {t("signOut")}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
        <Dialog
          open={personaDialogOpen}
          onOpenChange={(_event, data) => setPersonaDialogOpen(data.open)}
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Switch Persona</DialogTitle>
              <DialogContent>
                <PersonaSwitcher onSelect={() => setPersonaDialogOpen(false)} />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPersonaDialogOpen(false)}>
                  Close
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
        <PreferencesDialog
          open={preferencesOpen}
          onClose={() => setPreferencesOpen(false)}
        />
        <GettingStartedDialog
          open={gettingStartedOpen}
          onClose={() => setGettingStartedOpen(false)}
          onDismissForever={() =>
            setOnboarding((prev) => ({ ...prev, dismissed: true }))
          }
        />
      </div>
    </header>
  );
}
